import dynamic from 'next/dynamic';
import Image from 'next/image';

import { useRouter } from 'next/router';

import {
  Badge,
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import { styled } from '@mui/material/styles';

import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FacebookIcon from '@mui/icons-material/Facebook';

import Rating from '@mui/material/Rating';

import AmenitiesPanel from './AmenitiesPanel';
import CarsPanel from './CarsPanel';
import ReviewCard from './ReviewCard';
import HoursPanel from './HoursPanel';
import ServicesPanel from './ServicesPanel';

import { NextLinkComposed } from '../Link';

import gaEvent from '../../lib/gaEvent';


const DynamicDesktopPhotos = dynamic(() => import ('./DesktopPhotos'));


const LinkButton = styled(Button)(({ theme }) => ({
  fontWeight: 400,
  color: theme.palette.primary.dark,
  fontFamily: theme.typography.fontFamily,
}));


const DesktopProfile = ({ data, handleClickOpen }) => {
  
  const router = useRouter();

  return (
    <Container
      maxWidth="lg"
      sx={{
        display: {
          xs: 'none',
          md: 'block'
        }
      }}
    >
      <Box display="flex" alignItems="center" mt={4} mb={6}>
        <Box
          sx={{
            position: 'relative',
            minWidth: 240,
            minHeight: 240,
            width: 240,
            height: 240,
            bgcolor: "white",
            mr: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Image
            layout="fill"
            objectFit="cover"
            src={data.logo ? data.logo + '?w=240' : '/img/profile-placeholderx2.jpg'}
            title={data.name}
            alt={data.name}
            quality={100}
          />
        </Box>

        <Box flexDirection="column" justifyContent="center" alignItems="flex-start">
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 300,
              mb: 1,
            }}
          >
            {data.name}
          </Typography>

          <LinkButton
            startIcon={<LocationOnIcon />}
            href={`https://www.google.com/maps/dir/?api=1&destination=${data.address}`}
            target="_blank"
            rel="noopener"
          >
            {data.address}
          </LinkButton>

          <Box py={1} display="flex" alignItems="center">
            <Rating name="read-only" value={data.rating} readOnly precision={0.5} />
            <Box color="primary.light" ml={1}>
              {data.total_ratings} ratings
            </Box>

            <Stack
              sx={{
                ml: 4,
              }}
              direction="row"
              spacing={1}
            >
              {data.attributes.clean > 0 &&
                <Tooltip title="Clean & tidy" arrow={true}>
                  <Badge color="primary" overlap="circular" badgeContent={data.attributes.clean}>
                    <Paper
                      sx={{
                        borderRadius: '50%',
                        height: 40,
                        width: 40
                      }}
                    >
                      <Image
                        src={`/icons/reviews-thumbnail.svg`}
                        height={40}
                        width={40}
                        alt="Clean and tidy"
                      />
                    </Paper>
                  </Badge>
                </Tooltip>
              }
              {data.attributes.friendly > 0 &&
                <Tooltip title="Friendly" arrow={true}>
                  <Badge color="primary" overlap="circular" badgeContent={data.attributes.friendly}>
                    <Paper
                      sx={{
                        borderRadius: '50%',
                        borderWidth: 0,
                        height: 40,
                        width: 40
                      }}
                    >
                      <Image
                        src={`/icons/choose-a-mechanic.svg`}
                        height={40}
                        width={40}
                        alt="Friendly"
                      />
                    </Paper>
                  </Badge>
                </Tooltip>
              }
              {data.attributes.skill > 0 &&
                <Tooltip title="Skilled experts" arrow={true}>
                  <Badge color="primary" overlap="circular" badgeContent={data.attributes.skill}>
                    <Paper
                      sx={{
                        borderRadius: '50%',
                        border: 0,
                        height: 40,
                        width: 40,
                        position: 'relative'
                      }}
                    >
                      <Image
                        src={`/icons/choose-a-service.svg`}
                        layout="fill"
                        objectFit="cover"
                        alt="Skilled"
                      />
                    </Paper>
                  </Badge>
                </Tooltip>
              }
              {data.attributes.value > 0 &&
                <Tooltip title="Affordable" arrow={true}>
                  <Badge color="primary" overlap="circular" badgeContent={data.attributes.value}>
                    <Paper
                      sx={{
                        borderRadius: '50%',
                        border: 0,
                        height: 40,
                        width: 40,
                        position: 'relative'
                      }}
                    >
                      <Image
                        src={`/icons/post-requests.svg`}
                        height={40}
                        width={40}
                        alt="Affordable"
                      />
                    </Paper>
                  </Badge>
                </Tooltip>
              }
            </Stack>
          </Box>

          <Box display="flex" my={1}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => handleClickOpen()}
            >
              Message
            </Button>
            <Box mx={4}>
              <LinkButton
                startIcon={<PhoneIcon />}
                href={`tel:${data.telephone}`}
                onClick={() =>
                  gaEvent({
                    action: 'Call Phone',
                    category: 'Profile Page',
                    label: data.name,
                  })
                }
              >
                {data.telephone}
              </LinkButton>

              {data.mobile && data.telephone != data.mobile && (
                <LinkButton
                  startIcon={<PhoneIphoneIcon />}
                  href={`tel:${data.mobile}`}
                  onClick={() =>
                    gaEvent({
                      action: 'Call Mobile',
                      category: 'Profile Page',
                      label: data.name,
                    })
                  }
                >
                  {data.mobile}
                </LinkButton>
              )}

              {data.whatsapp && (
                <LinkButton
                  startIcon={<WhatsAppIcon />}
                  href={`https://wa.me/${data.mobile}?text=Hi, I found you're details on MechanicBuddy`}
                  onClick={() =>
                    gaEvent({
                      action: 'Chat Whatsapp',
                      category: 'Profile Page',
                      label: data.name,
                    })
                  }
                >
                  Click to Chat
                </LinkButton>
              )}

              {data.facebook_page && (
                <LinkButton
                  startIcon={<FacebookIcon />}
                  href={data.facebook_page}
                  onClick={() =>
                    gaEvent({
                      action: 'Facebook Page',
                      category: 'Profile Page',
                      label: data.name,
                    })
                  }
                >
                  View Page
                </LinkButton>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start" spacing={4}>
        <Grid item xs={12} md={8}>
          {!data.status === 'public' && (
            <Box p={3} mb={4} bgcolor="primary.main" color="#fff">
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1" component="h3">
                  This business is not registered and only listed
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  to={{
                    pathname: `/register-business/`,
                    query: {
                      name: data.name
                    }
                  }}
                  component={NextLinkComposed}
                >
                  Claim Business
                </Button>
              </Box>
            </Box>
          )}

          <Box p={3} mb={4} bgcolor="#fff">
            <Typography
              variant="h6"
              component="h2"
              gutterBottom
              sx={{
                color: 'text.secondary',
                fontWeight: 300,
                mb: 2,
              }}
            >
              About Us
            </Typography>
            {data.description && (
              <Typography variant="body2" gutterBottom>
                {data.description}
              </Typography>
            )}
            {!data.description && (
              <Typography variant="body2" gutterBottom>
                {`${data.name} is located at ${data.address}.${
                  data.telephone ? 'You can contact them at ' + data.telephone : ''
                }`}
              </Typography>
            )}

            {data.images.length > 0 && <DynamicDesktopPhotos images={data.images} businessName={data.name} />}
          </Box>

          {data.reviews.length > 0 && (
            <Box p={3} mb={4} bgcolor="primary.main" color="#fff">
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1" component="h3">
                  Help others by sharing your own experience
                </Typography>

                <Button
                  variant="contained"
                  color="primary"
                  component={NextLinkComposed}
                  to={{ pathname: `/write-review/${data.url_name}/` }}
                  sx={{
                    bgcolor: 'primary.light',
                    '&:hover': {
                      bgcolor: '#006978',
                    },
                  }}
                >
                  Write a Review
                </Button>
              </Box>
            </Box>
          )}

          {data.reviews.length > 0 && (
            <Box p={3} bgcolor="#fff" mb={4}>
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 300,
                  mb: 2,
                }}
              >
                Reviews for {data.name}
              </Typography>

              {data.reviews.map((review, index, arr) => (
                <ReviewCard key={review.id} review={review} divide={index != arr.length - 1} />
              ))}
            </Box>
          )}

          {data.reviews.length === 0 && (
            <Box
              bgcolor="white"
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              minHeight={300}
              my={4}
            >
              <Box mb={2} fontSize="h3.fontSize">
                No reviews as of yet
              </Box>
              <Box mb={2} textAlign="center">
                Be the first to leave a review for {data.name} and let them
                <br />
                know if you were satisfied with their service.
              </Box>
              <Button
                variant="contained"
                color="primary"
                sx={{
                  bgcolor: 'primary.light',
                  '&:hover': {
                    bgcolor: '#006978',
                  },
                }}
                component={NextLinkComposed}
                to={{ pathname: `/write-review/${data.url_name}/` }}
              >
                Write a Review
              </Button>
            </Box>
          )}
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
          sx={{
            mb: {
              xs: 0,
              md: 2
            }
          }}
        >
          <Box
            sx={{
              cursor: 'pointer',
              bgcolor: 'white',
              minHeight: 180,
              p: 1,
            }}
          >
            <Image
              width={380}
              height={250}
              src={`https://maps.googleapis.com/maps/api/staticmap?center=${data.address}&zoom=15&size=380x250&markers=label:${data.name[0]}|${data.address}|&key=AIzaSyDoOHc6XRVaQso0d8EybI4-xaAv965tCXA`}
              alt={`${data.name} location on map`}
              title={`${data.name} location on map`}
              onClick={() => router.push(`https://www.google.com/maps/dir/?api=1&destination=${data.address}`)}
            />
          </Box>

          {data.hours && <HoursPanel hours={data.hours} />}

          {/* Add Facebook panel */}

          {data.amenities.length > 0 && (
            <AmenitiesPanel amenities={data.amenities} />
          )}

          {data.services.length > 0 && <ServicesPanel services={data.services} />}

          {data.cars.length > 0 && <CarsPanel cars={data.cars} />}
        </Grid>
      </Grid>
    </Container>
  );
};

export default DesktopProfile;
